import { z } from 'zod';

// =============================================================================
// Two-Dimension Publishing Model (captureMode × timing)
// =============================================================================

// Capture Mode: what kind of content is published
export const captureModeOptions = ['snapshot', 'realtime', 'image'] as const;
export const captureModeSchema = z.enum(captureModeOptions);
export type CaptureMode = z.infer<typeof captureModeSchema>;

// Timing: when/how often content is updated
// Manual timing - user triggers publish explicitly
export const manualTimingSchema = z.object({
  kind: z.literal('manual'),
});
export type ManualTiming = z.infer<typeof manualTimingSchema>;

// Continuous timing - always up to date (live mode)
export const continuousTimingSchema = z.object({
  kind: z.literal('continuous'),
});
export type ContinuousTiming = z.infer<typeof continuousTimingSchema>;

// Scheduled timing variants (nested discriminated union on frequency)
export const hourlyTimingSchema = z.object({
  kind: z.literal('scheduled'),
  frequency: z.literal('hourly'),
});
export type HourlyTiming = z.infer<typeof hourlyTimingSchema>;

export const dailyTimingSchema = z.object({
  kind: z.literal('scheduled'),
  frequency: z.literal('daily'),
});
export type DailyTiming = z.infer<typeof dailyTimingSchema>;

export const weeklyTimingSchema = z.object({
  kind: z.literal('scheduled'),
  frequency: z.literal('weekly'),
  weeklyDay: z.enum(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']),
});
export type WeeklyTiming = z.infer<typeof weeklyTimingSchema>;

export const monthlyTimingSchema = z.object({
  kind: z.literal('scheduled'),
  frequency: z.literal('monthly'),
  monthlyWeek: z.enum(['1st', '2nd', '3rd', '4th']),
  monthlyDay: z.enum([
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ]),
});
export type MonthlyTiming = z.infer<typeof monthlyTimingSchema>;

// Scheduled timing discriminated union (by frequency)
export const scheduledTimingSchema = z.discriminatedUnion('frequency', [
  hourlyTimingSchema,
  dailyTimingSchema,
  weeklyTimingSchema,
  monthlyTimingSchema,
]);
export type ScheduledTiming = z.infer<typeof scheduledTimingSchema>;

// Full timing config discriminated union (by kind)
export const timingConfigSchema = z.discriminatedUnion('kind', [
  manualTimingSchema,
  continuousTimingSchema,
  // For scheduled, we need to use a base schema that zod can discriminate on
  z.object({
    kind: z.literal('scheduled'),
    frequency: z.enum(['hourly', 'daily', 'weekly', 'monthly']),
    weeklyDay: z
      .enum(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'])
      .optional(),
    monthlyWeek: z.enum(['1st', '2nd', '3rd', '4th']).optional(),
    monthlyDay: z
      .enum(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'])
      .optional(),
  }),
]);
export type TimingConfig = z.infer<typeof timingConfigSchema>;

// PublishConfig: captureMode × timing with constraints
// Snapshot: manual or scheduled (NOT continuous)
export const snapshotPublishConfigSchema = z.object({
  captureMode: z.literal('snapshot'),
  timing: z.union([manualTimingSchema, scheduledTimingSchema]),
});
export type SnapshotPublishConfig = z.infer<typeof snapshotPublishConfigSchema>;

// Realtime: any timing allowed
export const realtimePublishConfigSchema = z.object({
  captureMode: z.literal('realtime'),
  timing: z.union([manualTimingSchema, continuousTimingSchema, scheduledTimingSchema]),
});
export type RealtimePublishConfig = z.infer<typeof realtimePublishConfigSchema>;

// Image: manual only
export const imagePublishConfigSchema = z.object({
  captureMode: z.literal('image'),
  timing: manualTimingSchema,
});
export type ImagePublishConfig = z.infer<typeof imagePublishConfigSchema>;

// Full publish config discriminated union (by captureMode)
export const publishConfigSchema = z.discriminatedUnion('captureMode', [
  snapshotPublishConfigSchema,
  realtimePublishConfigSchema,
  imagePublishConfigSchema,
]);
export type PublishConfig = z.infer<typeof publishConfigSchema>;

// =============================================================================
// Legacy types (to be removed in subsequent tasks)
// =============================================================================

// Publishing layout options for visualizations
export const visualizationLayoutOptionsDTOSchema = z.object({
  title: z.boolean(),
  logo: z.boolean(),
  publishDate: z.boolean(),
  legend: z.boolean(),
});

export type VisualizationLayoutOptionsDTO = z.infer<typeof visualizationLayoutOptionsDTOSchema>;

export const visualizationAutoPublishOptions = [
  'never',
  'hourly',
  'daily',
  'weekly',
  'monthly',
  'realtime',
] as const;
export const visualizationAutoPublishSchema = z.enum(visualizationAutoPublishOptions);

export type VisualizationAutoPublish = z.infer<typeof visualizationAutoPublishSchema>;

export const scheduledAutoPublishOptions = ['hourly', 'daily', 'weekly', 'monthly'] as const;
export type ScheduledAutoPublish = (typeof scheduledAutoPublishOptions)[number];

const scheduledAutoPublishSet: ReadonlySet<string> = new Set(scheduledAutoPublishOptions);

export function isScheduledAutoPublish(
  value: VisualizationAutoPublish,
): value is ScheduledAutoPublish {
  return scheduledAutoPublishSet.has(value);
}

export const visualizationPublishTypeOptions = ['live', 'locked', 'image'] as const;
export const visualizationPublishTypeSchema = z.enum(visualizationPublishTypeOptions);

export type VisualizationPublishType = z.infer<typeof visualizationPublishTypeSchema>;

export const visualizationWeekDayOptions = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;
export const visualizationWeekDaySchema = z.enum(visualizationWeekDayOptions);

export type VisualizationWeekDay = z.infer<typeof visualizationWeekDaySchema>;

export const visualizationMonthWeekOptions = ['1st', '2nd', '3rd', '4th'] as const;
export const visualizationMonthWeekSchema = z.enum(visualizationMonthWeekOptions);

export type VisualizationMonthWeek = z.infer<typeof visualizationMonthWeekSchema>;

// Authentication type discriminator
export const visualizationAuthTypeOptions = ['none', 'password'] as const;
export const visualizationAuthTypeSchema = z.enum(visualizationAuthTypeOptions);

export type VisualizationAuthType = z.infer<typeof visualizationAuthTypeSchema>;

// Config as stored in database (internal use only - includes sensitive fields)
export const visualizationPublishConfigDTOSchema = z.object({
  layoutOptions: visualizationLayoutOptionsDTOSchema,
  visionStatement: z.string().max(1000).optional(),
  tempoBranding: z.boolean(),
  autoPublish: visualizationAutoPublishSchema,
  weeklyPublishDay: visualizationWeekDaySchema.optional(),
  monthlyPublishWeek: visualizationMonthWeekSchema.optional(),
  monthlyPublishDay: visualizationWeekDaySchema.optional(),
  scheduleTimezone: z
    .string()
    .refine(
      (tz) => {
        try {
          Intl.DateTimeFormat('en-US', { timeZone: tz });
          return true;
        } catch {
          return false;
        }
      },
      { message: 'Invalid IANA timezone identifier' },
    )
    .optional(),
  // Hidden fields configuration - fields to hide from published item cards
  hiddenFieldIds: z.array(z.string()).optional(),
  // Auth fields (stored)
  authType: visualizationAuthTypeSchema.default('none'),
  passwordHash: z.string().optional(), // Stored hash, NEVER returned to client
});

export type VisualizationPublishConfigDTO = z.infer<typeof visualizationPublishConfigDTOSchema>;

// Config for API responses (excludes sensitive fields)
export const visualizationPublishConfigResponseDTOSchema = visualizationPublishConfigDTOSchema.omit(
  {
    passwordHash: true, // Security: passwordHash never leaves the server
  },
);

export type VisualizationPublishConfigResponseDTO = z.infer<
  typeof visualizationPublishConfigResponseDTOSchema
>;

// Password operation discriminated union - makes illegal states unrepresentable
export const visualizationPasswordOperationSchema = z.discriminatedUnion('operation', [
  z.object({ operation: z.literal('keep') }), // Preserve existing (default if omitted)
  z.object({ operation: z.literal('set'), password: z.string().min(1) }), // Set new password
  z.object({ operation: z.literal('remove') }), // Clear password protection
]);

export type VisualizationPasswordOperation = z.infer<typeof visualizationPasswordOperationSchema>;

// Config for upsert requests (input from client)
export const visualizationPublishConfigUpsertDTOSchema = visualizationPublishConfigDTOSchema
  .omit({ authType: true, passwordHash: true }) // Client doesn't set these directly
  .extend({
    // Password management using discriminated union
    passwordOperation: visualizationPasswordOperationSchema.optional(),
  });

export type VisualizationPublishConfigUpsertDTO = z.infer<
  typeof visualizationPublishConfigUpsertDTOSchema
>;

// Published visualization DTO (internal use - includes sensitive config fields)
export const publishedVisualizationDTOSchema = z.object({
  organizationId: z.string().min(1),
  viewId: z.number().int(),
  visualizationId: z.number().int(),
  urlToken: z.string().length(12), // nanoid(12)
  userId: z.string().min(1),
  captureMode: captureModeSchema,
  config: visualizationPublishConfigDTOSchema, // Internal: includes passwordHash
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type PublishedVisualizationDTO = z.infer<typeof publishedVisualizationDTOSchema>;

// Published visualization response DTO (for API responses - excludes sensitive fields)
export const publishedVisualizationResponseDTOSchema = z.object({
  organizationId: z.string().min(1),
  viewId: z.number().int(),
  visualizationId: z.number().int(),
  urlToken: z.string().length(12),
  userId: z.string().min(1),
  captureMode: captureModeSchema,
  config: visualizationPublishConfigResponseDTOSchema, // Security: excludes passwordHash
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  capturedAt: z.string().datetime().nullable(), // When snapshot was captured (null for realtime)
  lastPublishedAt: z.string().datetime().nullable(), // Computed: capturedAt for scheduled, updatedAt for never, null for realtime
});

export type PublishedVisualizationResponseDTO = z.infer<
  typeof publishedVisualizationResponseDTOSchema
>;

// Status DTO (lightweight check - always returns 200)
export const publishedVisualizationStatusDTOSchema = z.object({
  isPublished: z.boolean(),
});

export type PublishedVisualizationStatusDTO = z.infer<typeof publishedVisualizationStatusDTOSchema>;

// Upsert DTO (for creating or updating a published visualization)
export const publishedVisualizationUpsertDTOSchema = z
  .object({
    captureMode: captureModeSchema,
    config: visualizationPublishConfigUpsertDTOSchema,
  })
  .strict();

export type PublishedVisualizationUpsertDTO = z.infer<typeof publishedVisualizationUpsertDTOSchema>;
